import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVault } from "../app/context/VaultContext";
import type { ConversationRef } from "../app/state/chatState";
import { apiFetchWorkspaces, apiFetchWorkspaceTopics, type WorkspaceListItem, type WorkspaceTopicItem } from "../lib/authApi";
import type { ContactRow } from "../lib/db";
import { AppIcon } from "./AppIcon";

type ForwardMessageModalProps = {
  previewText: string;
  contacts: ContactRow[];
  onClose: () => void;
  onConfirm: (target: ConversationRef) => Promise<boolean>;
};

type ForwardVaultPinInputProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
};

function ForwardVaultPinInput({ id, value, onChange, onSubmit }: ForwardVaultPinInputProps) {
  return (
    <div className="vault-section__pin-shell">
      <input
        id={id}
        className="vault-section__pin-input"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        enterKeyHint="done"
        pattern="[0-9]*"
        maxLength={5}
        name={`${id}-forward-pin`}
        data-1p-ignore="true"
        data-lpignore="true"
        data-form-type="other"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 5))}
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.length === 5) {
            onSubmit();
          }
        }}
        aria-label="Пин-код для vault"
      />
      <div className="vault-section__pin-slots" aria-hidden="true">
        {Array.from({ length: 5 }, (_, index) => {
          const filled = index < value.length;
          const active = index === value.length && value.length < 5;
          return (
            <span
              key={index}
              className={`vault-section__pin-slot${filled ? " is-filled" : ""}${active ? " is-active" : ""}`}
            >
              {filled ? "•" : ""}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function includesQuery(value: string, query: string): boolean {
  if (!query) return true;
  return value.toLowerCase().includes(query);
}

export function ForwardMessageModal({
  previewText,
  contacts,
  onClose,
  onConfirm,
}: ForwardMessageModalProps) {
  const { hasPin, unlocked, vaults, loading: vaultLoading, pinError, verifyPinAndUnlock } = useVault();
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();
  const [pinDraft, setPinDraft] = useState("");
  const [forwarding, setForwarding] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [workspacesError, setWorkspacesError] = useState<string | null>(null);
  const [expandedWorkspaceId, setExpandedWorkspaceId] = useState<string | null>(null);
  const [topicsByWorkspaceId, setTopicsByWorkspaceId] = useState<Record<string, WorkspaceTopicItem[]>>({});
  const [topicsLoadingByWorkspaceId, setTopicsLoadingByWorkspaceId] = useState<Record<string, boolean>>({});
  const [topicsErrorByWorkspaceId, setTopicsErrorByWorkspaceId] = useState<Record<string, string | null>>({});

  useEffect(() => {
    let cancelled = false;
    setWorkspacesLoading(true);
    setWorkspacesError(null);
    void apiFetchWorkspaces()
      .then((data) => {
        if (!cancelled) setWorkspaces(data.workspaces);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setWorkspaces([]);
          setWorkspacesError(error instanceof Error ? error.message : String(error));
        }
      })
      .finally(() => {
        if (!cancelled) setWorkspacesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (forwarding) return;
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [forwarding, onClose]);

  useEffect(() => {
    if (!successMessage) return;
    const timeout = window.setTimeout(() => onClose(), 2400);
    return () => window.clearTimeout(timeout);
  }, [successMessage, onClose]);

  const filteredContacts = useMemo(
    () => contacts.filter((contact) => includesQuery(contact.username, normalizedQuery)),
    [contacts, normalizedQuery],
  );

  const filteredVaults = useMemo(
    () => vaults.filter((vault) => includesQuery(vault.name, normalizedQuery)),
    [vaults, normalizedQuery],
  );

  const filteredWorkspaces = useMemo(
    () => workspaces.filter((workspace) => includesQuery(workspace.name, normalizedQuery)),
    [workspaces, normalizedQuery],
  );

  const handleExpandWorkspace = useCallback(
    async (workspaceId: string) => {
      setExpandedWorkspaceId((current) => (current === workspaceId ? null : workspaceId));
      if (topicsByWorkspaceId[workspaceId] || topicsLoadingByWorkspaceId[workspaceId]) return;
      setTopicsLoadingByWorkspaceId((current) => ({ ...current, [workspaceId]: true }));
      setTopicsErrorByWorkspaceId((current) => ({ ...current, [workspaceId]: null }));
      try {
        const data = await apiFetchWorkspaceTopics(workspaceId);
        setTopicsByWorkspaceId((current) => ({ ...current, [workspaceId]: data.topics }));
      } catch (error) {
        setTopicsErrorByWorkspaceId((current) => ({
          ...current,
          [workspaceId]: error instanceof Error ? error.message : String(error),
        }));
      } finally {
        setTopicsLoadingByWorkspaceId((current) => ({ ...current, [workspaceId]: false }));
      }
    },
    [topicsByWorkspaceId, topicsLoadingByWorkspaceId],
  );

  const forwardUnlockInFlightRef = useRef(false);

  const runForwardUnlockAttempt = useCallback(() => {
    if (pinDraft.length !== 5 || forwarding || vaultLoading || forwardUnlockInFlightRef.current) return;
    forwardUnlockInFlightRef.current = true;
    void verifyPinAndUnlock(pinDraft)
      .then(() => {
        setPinDraft("");
      })
      .finally(() => {
        forwardUnlockInFlightRef.current = false;
      });
  }, [pinDraft, forwarding, vaultLoading, verifyPinAndUnlock]);

  useEffect(() => {
    if (!hasPin || unlocked || pinDraft.length !== 5 || forwarding || vaultLoading) return;
    runForwardUnlockAttempt();
  }, [hasPin, unlocked, pinDraft, forwarding, vaultLoading, runForwardUnlockAttempt]);

  const handleConfirm = useCallback(
    async (target: ConversationRef, label: string) => {
      if (forwarding) return;
      setForwarding(true);
      const ok = await onConfirm(target);
      setForwarding(false);
      if (!ok) return;
      setSuccessMessage(`Переслано в ${label}`);
    },
    [forwarding, onConfirm],
  );

  return (
    <div className="forward-modal" role="dialog" aria-modal="true" aria-label="Переслать сообщение">
      <button
        type="button"
        className="forward-modal__backdrop"
        onClick={forwarding ? undefined : onClose}
        aria-label="Закрыть"
      />
      <div className="forward-modal__dialog">
        <div className="forward-modal__head">
          <div>
            <div className="forward-modal__eyebrow">переслать сообщение</div>
            <div className="forward-modal__title">
              {successMessage ? "Сообщение переслано" : forwarding ? "Пересылаем..." : "Выберите, куда отправить"}
            </div>
          </div>
          <button
            type="button"
            className="forward-modal__close"
            onClick={forwarding ? undefined : onClose}
            aria-label="Закрыть"
            disabled={forwarding}
          >
            <AppIcon name="x" size={16} />
          </button>
        </div>

        {successMessage ? (
          <div className="forward-modal__success" aria-live="polite">
            <span className="forward-modal__success-icon">
              <AppIcon name="check" size={18} />
            </span>
            <span className="forward-modal__success-text">{successMessage}</span>
          </div>
        ) : null}

        <div className="forward-modal__preview">
          <span className="forward-modal__preview-label">Содержимое</span>
          <div className="forward-modal__preview-body">{previewText || "..."}</div>
        </div>

        <input
          type="text"
          className="forward-modal__search"
          placeholder="Поиск контакта, workspace или vault..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          disabled={forwarding}
        />

        <div className="forward-modal__sections">
          <section className="forward-modal__section">
            <div className="forward-modal__section-title">Контакты</div>
            {filteredContacts.length === 0 ? (
              <div className="forward-modal__empty">Ничего не найдено</div>
            ) : (
              <div className="forward-modal__list">
                {filteredContacts.map((contact) => (
                  <button
                    key={contact.user_id}
                    type="button"
                    className="forward-modal__item"
                    onClick={() => void handleConfirm({ kind: "dm", peerId: contact.user_id }, `@${contact.username}`)}
                    disabled={forwarding}
                  >
                    <span className="forward-modal__item-main">@{contact.username}</span>
                    <span className="forward-modal__item-meta">DM</span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="forward-modal__section">
            <div className="forward-modal__section-title">Workspaces</div>
            {workspacesLoading ? (
              <div className="forward-modal__empty">Загрузка workspaces...</div>
            ) : workspacesError ? (
              <div className="forward-modal__error">{workspacesError}</div>
            ) : filteredWorkspaces.length === 0 ? (
              <div className="forward-modal__empty">Ничего не найдено</div>
            ) : (
              <div className="forward-modal__list">
                {filteredWorkspaces.map((workspace) => {
                  const topics = topicsByWorkspaceId[workspace.id] ?? [];
                  const workspaceMatchesQuery = includesQuery(workspace.name, normalizedQuery);
                  const filteredTopics = workspaceMatchesQuery || !normalizedQuery
                    ? topics
                    : topics.filter((topic) => includesQuery(topic.name, normalizedQuery));
                  const isExpanded = expandedWorkspaceId === workspace.id;
                  return (
                    <div key={workspace.id} className="forward-modal__workspace">
                      <button
                        type="button"
                        className="forward-modal__item forward-modal__item--workspace"
                        onClick={() => void handleExpandWorkspace(workspace.id)}
                        aria-expanded={isExpanded}
                        disabled={forwarding}
                      >
                        <span className="forward-modal__item-main">{workspace.name}</span>
                        <span className="forward-modal__item-meta">topics</span>
                      </button>
                      {isExpanded ? (
                        <div className="forward-modal__topics">
                          {topicsLoadingByWorkspaceId[workspace.id] ? (
                            <div className="forward-modal__empty">Загрузка топиков...</div>
                          ) : topicsErrorByWorkspaceId[workspace.id] ? (
                            <div className="forward-modal__error">{topicsErrorByWorkspaceId[workspace.id]}</div>
                          ) : filteredTopics.length === 0 ? (
                            <div className="forward-modal__empty">Топики не найдены</div>
                          ) : (
                            filteredTopics.map((topic) => (
                              <button
                                key={topic.id}
                                type="button"
                                className="forward-modal__item forward-modal__item--topic"
                                onClick={() =>
                                  void handleConfirm({
                                    kind: "workspace-topic",
                                    workspaceId: workspace.id,
                                    topicId: topic.id,
                                  }, `#${topic.name}`)
                                }
                                disabled={forwarding}
                              >
                                <span className="forward-modal__item-main">#{topic.name}</span>
                                <span className="forward-modal__item-meta">{workspace.name}</span>
                              </button>
                            ))
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {hasPin ? (
            <section className="forward-modal__section">
              <div className="forward-modal__section-title">Vaults</div>
              {unlocked ? (
                filteredVaults.length === 0 ? (
                  <div className="forward-modal__empty">Ничего не найдено</div>
                ) : (
                  <div className="forward-modal__list">
                    {filteredVaults.map((vault) => (
                      <button
                        key={vault.id}
                        type="button"
                        className="forward-modal__item"
                        onClick={() => void handleConfirm({ kind: "vault", vaultId: vault.id }, vault.name)}
                        disabled={forwarding}
                      >
                        <span className="forward-modal__item-main">{vault.name}</span>
                        <span className="forward-modal__item-meta">vault</span>
                      </button>
                    ))}
                  </div>
                )
              ) : (
                <div className="forward-modal__vault-lock">
                  <div className="forward-modal__hint">Введите пин-код, чтобы открыть vaults для пересылки.</div>
                  <ForwardVaultPinInput
                    id="forward-vault-pin"
                    value={pinDraft}
                    onChange={setPinDraft}
                    onSubmit={runForwardUnlockAttempt}
                  />
                  {pinError ? <div className="forward-modal__error">{pinError}</div> : null}
                </div>
              )}
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
